import AppContainer from "features/home/AppContainer";
import Landing from "features/home/Landing";
import {
  AiManagement,
  AppUpdateList,
  CompanyProfile,
  CustomerAnalysis,
  CustomerImportHistory,
  GroupDetail,
  GroupList,
  Integration,
  IntegrationAttributionLink,
  IntegrationConnectDiscord,
  IntegrationConnectDiscordUser,
  IntegrationConnectSlack,
  IntegrationConnectLinear,
  IntegrationConnectJira,
  IntegrationInitJira,
  IntegrationDiscord,
  IntegrationDiscordWebhook,
  IntegrationGithub,
  IntegrationLinear,
  IntegrationJira,
  IntegrationMailchimp,
  IntegrationSlack,
  IntegrationTelegramNotification,
  IntegrationXmtp,
  InterfaceTheme,
  Macros,
  MainMenu,
  OnChainDataSource,
  Organization,
  OrganizationCreation,
  PermissionControl,
  Profile,
  TelegramBot,
  TelegramAnalyticsBot,
  TelegramSetting,
  TelegramAnalyticsSetting,
  TelegramNotificationSetting,
  CommunityAnalysis,
  DAppTracking,
  EcoScan,
  EcoScanDetail,
  Security,
  ServerCallback,
  ServiceReportPage,
  SettingSupportCenter,
  TicketList,
  TicketType,
  TrafficReportObjective,
  WebsiteTrafficSetting,
  DappActivityTracking,
  AddContract,
  DAppTraffic,
  XBehavior,
  NotificationCenter,
  NotificationReport,
  NotificationCreateContainer,
  NotificationDetailContainer,
} from "features/metadesk";
import NotFound from "features/metadesk/components/NotFound";
import UnderMaintenance from "features/metadesk/components/UnderMaintenance";
import CreateGroup from "features/metadesk/group/CreateGroup/CreateGroup";
import MarketingOnboardingSetStepContainer from "features/metadesk/Onboarding/MarketingOnboarding/MarketingOnboardingSetStepContainer";
import Onboarding from "features/metadesk/Onboarding/Onboarding";
import ServiceOnboardingSetStepContainer from "features/metadesk/Onboarding/ServiceOnboarding/ServiceOnboardingSetStepContainer";
import AddIntegration from "features/metadesk/settings/IntegrationPage/AddIntegration";
import IntegrationWidgetAdd from "features/metadesk/settings/integrations/widget/IntegrationWidgetAdd";
import IntegrationWidgetSetting from "features/metadesk/settings/integrations/widget/IntegrationSetting/IntegrationSetting";
import IntegrationOptInAdd from "features/metadesk/settings/integrations/websiteOptInSection/IntegrationOptInAdd";
import IntegrationOptInSetting from "features/metadesk/settings/integrations/websiteOptInSection/IntegrationSetting/IntegrationSetting";
import Billing from "features/metadesk/settings/Billing/Billing";
import ManagePlan from "features/metadesk/settings/Billing/ManagePlan";
import _ from "lodash";
import React from "react";
import { FormattedMessage } from "react-intl";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import envConfig from "./config";
import StatusLabel from "components/StatusLabel/StatusLabel";
import { Box } from "@mui/material";
import XCampaign from "features/metadesk/XCampaign/XCampaign";
import CreateXCampaign from "features/metadesk/XCampaign/CreateXCampaign";
import EditXCampaign from "features/metadesk/XCampaign/EditXCampaign";
import XPerformance from "features/metadesk/XCampaign/XPerformance";
import XShared from "features/metadesk/XCampaign/XShared";
import CustomerList from "features/metadesk/importList/CustomerList";
import {
  KolLibrary,
  KolGroups,
  KolGroupMemberList,
  KeywordMonitoring,
  KeywordAnalysis,
} from "features/metadesk/Kol";
import AiTools from "features/metadesk/AiService/tools/AiTools";
import KnowledgeBase from "features/metadesk/AiService/knowledgeBase/KnowledgeBase";
import TrainingArea from "features/metadesk/AiService/trainingArea/TrainingArea";
import InternalSettings from "features/metadesk/settings/InternalSettings";

const envEntity = process.env.REACT_APP_ENTITY;
const isCustomDomain = Boolean(envEntity);

const transformConfig = (config) => {
  const transform = (route, parentUpgrade = {}, parentAllowed = {}) => {
    const {
      path,
      element,
      children,
      feature,
      handle,
      upgrade,
      disabled,
      index,
      ecosystems,
      needEditPermission,
      allowed,
      allowedEntities,
    } = route;

    const mergedAllowed = { ...parentAllowed, ...allowed };
    const mergedUpgrade = { ...parentUpgrade, ...upgrade };
    const wrappedElement = (
      <PermissionControl
        upgrade={mergedUpgrade} //check config to upgrade
        feature={feature} // check permission to hide and redirect to 404
        allowed={mergedAllowed} //check config to hide and redirect to 404
        ecosystems={ecosystems}
        disabled={disabled}
        allowedEntities={allowedEntities}
        needEditPermission={needEditPermission}
      >
        {element}
      </PermissionControl>
    );
    const transformedChildren = children
      ? children.map((child) => transform(child, mergedUpgrade, mergedAllowed))
      : undefined;

    return {
      path,
      element: element ? wrappedElement : null,
      children: transformedChildren,
      index,
      handle,
    };
  };
  return transform(config);
};

const serviceConfig = {
  path: "service",
  title: "Community",
  icon: <img src={require("assets/img/module_ic/community.svg").default} />,
  activeIcon: (
    <img src={require("assets/img/module_ic/community_active.svg").default} />
  ),
  element: <MainMenu />,
  upgrade: { "modules.service": true },
  children: [
    {
      path: "ticket",
      feature: "tickets",
      isMenu: true,
      title: <FormattedMessage id="global.function.tickets" />,
      category: "ticket", // for stats count
      children: [
        {
          title: <FormattedMessage id="global.ticketStatus.new" />,
          path: "new",
          feature: "tickets",
          element: <TicketList ticketStatus="new" />,
        },
        {
          title: <FormattedMessage id="global.ticketStatus.ongoing" />,
          path: "ongoing",
          feature: "tickets",
          element: <TicketList ticketStatus="ongoing" />,
        },
        {
          title: <FormattedMessage id="global.ticketStatus.done" />,
          path: "done",
          feature: "tickets",
          element: <TicketList ticketStatus="done" />,
        },
        {
          title: <FormattedMessage id="global.ticketStatus.all" />,
          path: "all",
          feature: "tickets",
          element: <TicketList ticketStatus="all" />,
        },
      ],
    },
    {
      path: "community-analysis",
      allowed: {
        "serviceModule.discordAnalytics.enabled": true,
        "serviceModule.telegramAnalytics.enabled": true,
      },
      feature: "communityAnalysis",
      title: <FormattedMessage id="global.function.communityAnalysis" />,
      element: <CommunityAnalysis />,
    },
    {
      path: "reports",
      title: <FormattedMessage id="global.function.serviceReport" />,
      feature: "ticketReport",
      element: <ServiceReportPage />,
    },
  ],
};

const kolConfig = {
  path: "kol",
  title: "Kol",
  icon: <img src={require("assets/img/module_ic/marketing.svg").default} />,
  activeIcon: (
    <img src={require("assets/img/module_ic/marketing_active.svg").default} />
  ),
  element: <MainMenu />,
  upgrade: { "modules.kol": true },
  children: [
    {
      path: "xCampaign",
      title: <FormattedMessage id="global.function.xCampaignTracking.title" />,
      feature: "xCampaigns",
      upgrade: { "kolModule.xCampaign.enabled": true, demoSite: false },
      children: [
        { index: true, path: "", element: <XCampaign /> },
        {
          path: "add",
          feature: "xCampaigns",
          needEditPermission: true,
          element: <CreateXCampaign />,
        },
        {
          path: "add/:campaignId",
          feature: "xCampaigns",
          needEditPermission: true,
          element: <EditXCampaign />,
        },
        {
          path: "performance/:campaignId",
          element: <XPerformance />,
        },
      ],
    },
    {
      path: "kolLibrary",
      title: <FormattedMessage id="kolLibrary.title" />,
      feature: "kol",
      upgrade: { "kolModule.xCampaign.enabled": true, demoSite: true },
      element: <KolLibrary />,
    },
    {
      path: "kolGroups",
      title: <FormattedMessage id="kolGroups.title" />,
      feature: "kol",
      upgrade: { "kolModule.xCampaign.enabled": true, demoSite: true },
      children: [
        {
          index: true,
          path: "",
          element: <KolGroups />,
          needEditPermission: true,
        },
        {
          path: ":groupId",
          feature: "kol",
          element: <KolGroupMemberList />,
        },
      ],
    },
    {
      path: "keywordMonitoring",
      title: <FormattedMessage id="global.keywordMonitoring.title" />,
      feature: "kol",
      upgrade: { "kolModule.xCampaign.enabled": true, demoSite: true },
      children: [
        {
          index: true,
          path: "",
          element: <KeywordMonitoring />,
          needEditPermission: true,
        },
        {
          path: "analysis/:keywordId",
          feature: "kol",
          element: <KeywordAnalysis />,
        },
      ],
    },
  ],
};

const notificationConfig = {
  path: "notification",
  title: "Notification",
  icon: (
    <img src={require("assets/img/module_ic/notification_icon.svg").default} />
  ),
  activeIcon: (
    <img
      src={require("assets/img/module_ic/notification_icon_active.svg").default}
    />
  ),
  element: <MainMenu />,
  upgrade: { "modules.notification": true },
  children: [
    {
      path: "notification-center",
      feature: "notifications",
      element: <NotificationCenter />,
      index: true,
      title: <FormattedMessage id="global.function.notificationCenter" />,
    },
    {
      path: "notification-report",
      needEditPermission: true,
      feature: "notificationPerformance",
      element: <NotificationReport></NotificationReport>,
      title: <FormattedMessage id="global.function.notificationReport" />,
    },
    {
      path: "notification-detail/create",
      feature: "notifications",
      element: <NotificationCreateContainer />,
    },
    {
      path: "notification-detail/:notificationId",
      feature: "notifications",
      element: <NotificationDetailContainer />,
    },
  ],
};

const analyticsConfig = {
  path: "analytics",
  title: "Footprints",
  icon: <img src={require("assets/img/module_ic/footprints.svg").default} />,
  activeIcon: (
    <img src={require("assets/img/module_ic/footprints_active.svg").default} />
  ),
  element: <MainMenu />,
  upgrade: { "modules.analytics": true },
  children: [
    {
      path: "website",
      title: <FormattedMessage id="global.function.websiteTraffic" />,
      feature: "webTraffic",
      children: [
        {
          index: true,
          path: "",
          feature: "webTraffic",
          element: <DAppTraffic />,
        },
        // {
        //   path: "objective",
        //   feature: "campaignPerformance",
        //   element: <TrafficReportObjective />,
        // },
      ],
    },
    {
      path: "dapp-tracking",
      feature: "dappTracking",
      ecosystems: ["evm"],
      title: <FormattedMessage id="global.function.dappTracking" />,
      element: <DAppTracking />,
    },
    {
      path: "settings",
      isMenu: true,
      title: <FormattedMessage id="global.function.settings" />,
      ecosystems: ["evm", "solana"],
      children: [
        {
          title: <FormattedMessage id="global.function.objective" />,
          path: "website-traffic",
          feature: "webTraffic",
          icon: <i className="meta-crm-icon-ic_websiteTraffic font-size-16" />,
          children: [
            {
              index: true,
              path: "",
              feature: "webTraffic",
              ecosystems: ["evm", "solana"],
              element: <WebsiteTrafficSetting />,
            },
            {
              path: "create-objective",
              ecosystems: ["evm", "solana"],
              needEditPermission: true,
              feature: "webTraffic",
              element: <TrafficReportObjective />,
            },
          ],
        },
        {
          title: <FormattedMessage id="global.function.dappTracking" />,
          path: "dapp-activity-tracking",
          feature: "dappTracking",
          ecosystems: ["evm"],
          icon: <i className="meta-crm-icon-ic_dapp font-size-16" />,
          children: [
            {
              index: true,
              path: "",
              feature: "dappTracking",
              ecosystems: ["evm"],
              element: <DappActivityTracking />,
            },
            {
              path: "add-contract",
              ecosystems: ["evm"],
              needEditPermission: true,
              feature: "dappTracking",
              element: <AddContract />,
            },
          ],
        },
      ],
    },
  ],
};

const ecoScanConfig = {
  path: "ecoScan",
  title: "EcoScan",
  icon: <img src={require("assets/img/module_ic/ic_ecoScan.svg").default} />,
  activeIcon: (
    <img src={require("assets/img/module_ic/ic_ecoScan_active.svg").default} />
  ),
  element: <MainMenu />,
  children: [
    {
      path: "overview",
      title: <FormattedMessage id="global.function.overview" />,
      feature: "ecoScan",
      allowedEntities: envConfig.allowedEntities.ecoScan,
      children: [
        {
          index: true,
          path: "",
          feature: "ecoScan",
          element: <EcoScan type="overview" />,
        },
      ],
    },
    {
      path: "community-analysis",
      title: <FormattedMessage id="global.function.customerAnalysis" />,
      feature: "ecoScan",
      allowedEntities: envConfig.allowedEntities.ecoScan,
      children: [
        {
          index: true,
          path: "",
          feature: "ecoScan",
          element: <EcoScan type="communityAnalysis" />,
        },
        {
          path: "detail/:subEntityName",
          feature: "ecoScan",
          element: <EcoScanDetail type="communityAnalysis" />,
        },
      ],
    },
    {
      path: "website-traffic",
      title: <FormattedMessage id="global.function.websiteTraffic" />,
      feature: "ecoScan",
      allowedEntities: envConfig.allowedEntities.ecoScan,
      children: [
        {
          index: true,
          path: "",
          feature: "ecoScan",
          element: <EcoScan type="websiteTraffic" />,
        },
        {
          path: "detail/:subEntityName",
          feature: "ecoScan",
          element: <EcoScanDetail type="websiteTraffic" />,
        },
      ],
    },
    {
      path: "dapp-tracking",
      title: <FormattedMessage id="global.function.dappTracking" />,
      feature: "ecoScan",
      allowedEntities: envConfig.allowedEntities.ecoScan,
      children: [
        {
          index: true,
          path: "",
          feature: "ecoScan",
          element: <EcoScan type="dAppTracking" />,
        },
        {
          path: "detail/:subEntityName",
          feature: "ecoScan",
          element: <EcoScanDetail type="dAppTracking" />,
        },
      ],
    },
  ],
};

const advertiseConfig = {
  path: "advertise",
  title: "Ads",
  upgrade: { "advertise.enabled": true },
  icon: <i className="meta-crm-icon-ic_advertise font-size-20" />,
  element: <MainMenu />,
  children: [{}],
};

const customersConfig = {
  path: "customers",
  title: "CDP",
  upgrade: { "modules.CDP": true },
  icon: <img src={require("assets/img/module_ic/cdp.svg").default} />,
  activeIcon: (
    <img src={require("assets/img/module_ic/cdp_active.svg").default} />
  ),
  element: <MainMenu />,
  children: [
    {
      path: "list",
      title: <FormattedMessage id="global.function.customerList" />,
      feature: "customerList",
      element: <CustomerList />,
    },
    {
      path: "analysis",
      feature: "customerReport",
      title: <FormattedMessage id="global.function.customerAnalysis" />,
      element: <CustomerAnalysis />,
    },
    {
      path: "X-analysis",
      feature: "customerReport",
      title: "X Account Analysis",
      allowedEntities: ["defitax-demo"],
      element: <XBehavior />,
    },
    {
      path: "history",
      feature: "customerList",
      title: <FormattedMessage id="global.function.importHistory" />,
      needEditPermission: true,
      element: <CustomerImportHistory />,
    },
    // {
    //   path: "segment/create",
    //   feature: "segmentation",
    //   needEditPermission: true,
    //   element: <CreateGroup />,
    // },
    // {
    //   path: "segment/:groupId",
    //   feature: "segmentation",
    //   element: <GroupDetail />,
    // },
    // {
    //   path: "segment",
    //   title: <FormattedMessage id="global.function.segmentation" />,
    //   feature: "segmentation",
    //   ecosystems: ["evm"],
    //   element: <GroupList />,
    // },
  ],
};

const settingsConfig = {
  path: "settings",
  title: "Settings",
  icon: <img src={require("assets/img/module_ic/setting.svg").default} />,
  activeIcon: (
    <img src={require("assets/img/module_ic/setting_active.svg").default} />
  ),
  element: <MainMenu />,
  children: [
    {
      path: "service-module",
      title: <FormattedMessage id="global.module.community" />,
      isMenu: true,
      upgrade: {
        "modules.service": true,
      },
      children: [
        {
          title: <FormattedMessage id="global.function.ticketSetting" />,
          icon: <i className="meta-crm-icon-ic_ticket font-size-16" />,
          path: "ticket",
          feature: "ticketSettings",
          element: <TicketType />,
        },
        {
          path: "macros",
          title: <FormattedMessage id="globol.module.macros" />,
          icon: <i className="meta-crm-icon-ic_macro font-size-16" />,
          feature: "macros",
          element: <Macros />,
        },
        // {
        //   path: "security",
        //   title: <FormattedMessage id="securitySetting.security.title" />,
        //   type: "security",
        //   icon: <i className="meta-crm-icon-ic_safe font-size-16" />,
        //   feature: "security",
        //   element: <Security />,
        // },
      ],
    },
    {
      path: "ai",
      title: "AI Settings",
      isMenu: true,
      upgrade: { "serviceModule.aiSupport.enabled": true },
      children: [
        //for route
        {
          title: <FormattedMessage id="global.aiTools.title" />,
          icon: <i className="meta-crm-icon-ic_aiRound font-size-16" />,
          path: "tools",
          feature: "aiManagement",
          element: <AiTools />,
          upgrade: { "serviceModule.aiSupport.enabled": true },
        },
        {
          title: <FormattedMessage id="global.function.knowledgeBase" />,
          icon: <i className="meta-crm-icon-ic_knowledge-base font-size-16" />,
          path: "knowledgeBase",
          feature: "aiManagement",
          element: <KnowledgeBase />,
          upgrade: { "serviceModule.aiSupport.enabled": true },
        },
        {
          title: <FormattedMessage id="global.module.trainingArea" />,
          icon: <i className="meta-crm-icon-ic_training-area font-size-16" />,
          path: "trainingArea",
          feature: "aiManagement",
          element: <TrainingArea />,
          upgrade: { "serviceModule.aiSupport.enabled": true },
        },
      ],
    },
    {
      path: "general",
      title: <FormattedMessage id="global.function.generalSetting" />,
      isMenu: true,
      children: [
        //for route
        {
          path: "app/update-list",
          feature: "integration",
          handle: { hideMenu: true },
          element: <AppUpdateList />,
        },
        {
          title: <FormattedMessage id="global.function.companySetting" />,
          icon: <i className="meta-crm-icon-ic_people font-size-16" />,
          path: "company/profile",
          feature: "profileCompany",
          element: <CompanyProfile />,
        },
        {
          title: <FormattedMessage id="global.function.dataSourceSetting" />,
          icon: <i className="meta-crm-icon-ic_data font-size-16" />,
          path: "onchain-data",
          feature: "onchainData",
          element: <OnChainDataSource />,
        },
        {
          path: "integration",
          feature: "integration",
          title: <FormattedMessage id="integrationSetting.integration.title" />,
          icon: <i className="meta-crm-icon-ic_integration font-size-16" />,
          children: [
            {
              path: "",
              index: true,
              feature: "integration",
              needEditPermission: true,
              element: <Integration />,
            },
            {
              path: "add",
              index: true,
              feature: "integration",
              needEditPermission: true,
              element: <AddIntegration />,
            },
            {
              path: "discordWebhook",
              feature: "integration",
              element: <IntegrationDiscordWebhook />,
            },

            {
              path: "discord",
              feature: "integration",
              handle: { hideMenu: true },
              element: <IntegrationDiscord />,
            },
            {
              path: "slack",
              feature: "integration",
              handle: { hideMenu: true },
              upgrade: { "modules.service": true },
              element: <IntegrationSlack />,
            },
            {
              path: "mailchimp",
              handle: { hideMenu: true },
              feature: "integration",
              element: <IntegrationMailchimp />,
            },

            {
              path: "github",
              feature: "integration",
              element: <IntegrationGithub />,
            },
            {
              path: "linear",
              feature: "integration",
              handle: { hideMenu: true },
              upgrade: { "modules.service": true },
              element: <IntegrationLinear />,
            },
            {
              path: "jira",
              feature: "integration",
              handle: { hideMenu: true },
              upgrade: { "modules.service": true },
              element: <IntegrationJira />,
            },
            {
              path: "attribution",
              feature: "integration",
              handle: { hideMenu: true },
              upgrade: {
                "modules.analytics": true,
                "analyticsModule.attribution.enabled": true,
              },
              element: <IntegrationAttributionLink isSetting={false} />,
            },
            {
              path: "add",
              children: [
                {
                  path: "widget",
                  feature: "integration",
                  element: <IntegrationWidgetAdd />,
                },
                {
                  path: "widget/:widgetId",
                  feature: "integration",
                  element: <IntegrationWidgetAdd />,
                },
                {
                  path: "websiteOptIn",
                  feature: "integration",
                  element: <IntegrationOptInAdd />,
                },
              ],
            },
            {
              path: "update",
              children: [
                {
                  path: "mailchimp",
                  feature: "integration",
                  element: <IntegrationMailchimp isSetting={true} />,
                },
                {
                  path: "xmtp",
                  feature: "integration",
                  upgrade: {
                    "modules.notification": true,
                    "notificationModule.xmtp.enabled": true,
                  },
                  element: <IntegrationXmtp />,
                },

                {
                  path: "discordWebhook",
                  feature: "integration",
                  upgrade: {
                    "modules.service": true,
                  },
                  element: <IntegrationDiscordWebhook />,
                },
                {
                  path: "discord",
                  feature: "integration",
                  upgrade: {
                    "modules.service": true,
                  },
                  element: <IntegrationDiscord isSetting={true} />,
                },
                {
                  path: "slack",
                  feature: "integration",
                  upgrade: {
                    "modules.service": true,
                  },
                  element: <IntegrationSlack isSetting={true} />,
                },
                {
                  path: "telegramNotification",
                  feature: "integration",
                  upgrade: {
                    "modules.service": true,
                  },
                  element: <IntegrationTelegramNotification isSetting={true} />,
                },
                {
                  path: "linear",
                  feature: "integration",
                  upgrade: {
                    "modules.service": true,
                  },
                  element: <IntegrationLinear isSetting={true} />,
                },
                {
                  path: "jira",
                  feature: "integration",
                  upgrade: {
                    "modules.service": true,
                  },
                  element: <IntegrationJira isSetting={true} />,
                },
                {
                  path: "attribution",
                  feature: "integration",
                  upgrade: {
                    "modules.analytics": true,
                    "analyticsModule.attribution.enabled": true,
                  },
                  element: <IntegrationAttributionLink isSetting={true} />,
                },
                {
                  path: "telegram",
                  element: <TelegramBot />,
                  feature: "integration",
                  title: "Telegram Bot",
                  icon: (
                    <i className="meta-crm-icon-ic_telegram font-size-12" />
                  ),
                },
                {
                  path: "telegramAnalytics",
                  element: <TelegramAnalyticsBot />,
                },
                {
                  path: "support-center",
                  feature: "integration",
                  element: <InterfaceTheme />,
                },
                {
                  path: "widget/:widgetId",
                  feature: "integration",
                  element: <IntegrationWidgetSetting />,
                },
                {
                  path: "websiteOptIn",
                  feature: "integration",
                  element: <IntegrationOptInSetting />,
                },
              ],
            },
          ],
        },

        {
          path: "organization",
          title: (
            <FormattedMessage id="organizationSetting.organization.title" />
          ),
          feature: "team",
          icon: <i className="meta-crm-icon-ic_organization font-size-16" />,
          children: [
            {
              index: true,
              path: "",
              feature: "team",
              element: <Organization />,
            },
            {
              path: "create",
              feature: "team",
              needEditPermission: true,
              element: <OrganizationCreation />,
            },
            {
              path: "create/:id",
              feature: "team",
              needEditPermission: true,
              element: <OrganizationCreation />,
            },
          ],
        },
        {
          title: <FormattedMessage id="billingSetting.billing.title" />,
          icon: <i className="meta-crm-icon-ic_wallet font-size-16" />,
          feature: "billing",
          allowedEntities: envConfig.allowedEntities.billing,
          path: "billing",
          children: [
            {
              index: true,
              path: "",
              feature: "billing",
              element: <Billing />,
            },
            {
              path: "manage-plan",
              feature: "billing",
              element: <ManagePlan />,
            },
          ],
        },
      ],
    },
    {
      path: "customization",
      feature: "integration",
      children: [
        {
          path: "support-center",
          feature: "integration",
          handle: { hideMenu: true },
          element: <SettingSupportCenter />,
        },
        {
          path: "telegramSetting",
          feature: "integration",
          handle: { hideMenu: true },
          element: <TelegramSetting />,
        },
        {
          path: "telegramNotification",
          feature: "integration",
          handle: { hideMenu: true },
          element: <TelegramNotificationSetting />,
        },
        {
          path: "telegramAnalyticsSetting",
          feature: "customization",
          handle: { hideMenu: true },
          element: <TelegramAnalyticsSetting />,
        },
      ],
    },
    {
      path: "config",
      feature: "integration",
      children: [
        {
          path: "jira",
          feature: "integration",
          handle: { hideMenu: true },
          element: <IntegrationInitJira />,
        },
      ],
    },
  ],
};

const accountConfig = {
  path: "account",
  title: "Account",
  icon: (
    <img
      src={require("assets/img/roles_ic/ic_alpaca-1.svg").default}
      style={{ width: 16 }}
    />
  ),
  alwaysShow: true,
  element: <MainMenu />,
  children: [
    {
      path: "",
      title: "",
      element: <div />,
    },
    {
      path: "profile",
      title: <FormattedMessage id="global.function.profile" />,
      feature: "profileUser",
      icon: <i className="meta-crm-icon-ic_profile font-size-16" />,
      element: <Profile />,
    },
  ],
};

export const routeConfig = {
  service: serviceConfig,
  notification: notificationConfig,
  analytics: analyticsConfig,
  ecoScan: ecoScanConfig,
  kol: kolConfig,
  advertise: advertiseConfig,
  customers: customersConfig,
  divider: "divider",
  settings: settingsConfig,
  account: accountConfig,
};

export const createRouteConfig = () => {
  const appChildren = [
    {
      path: "error",
      element: <NotFound />,
    },
    {
      path: "",
      element: <MainMenu />,
      children: [
        {
          index: true,
          element: <PermissionControl defaultPage></PermissionControl>,
        },
      ],
    },
    {
      path: "internalSettings",
      element: <InternalSettings />,
    },
  ];
  for (let module of _.values(routeConfig)) {
    appChildren.push(transformConfig(module));
  }

  const routes = [
    {
      path: "error",
      element: <NotFound />,
    },
    {
      path: "callback",
      element: <ServerCallback />,
    },
    {
      path: isCustomDomain ? "/" : "/:entity",
      element: <AppContainer />,
      errorElement: <NotFound />,
      children: appChildren,
    },
    {
      path: "/maintenance ",
      element: <UnderMaintenance />,
    },
    {
      path: "/",
      errorElement: <NotFound />,
      element: <Landing />,
    },
    {
      path: "/connectDiscord",
      element: <IntegrationConnectDiscord />,
    },
    {
      path: "/fetchDiscordUser",
      element: <IntegrationConnectDiscordUser />,
    },
    {
      path: "/connectSlack",
      element: <IntegrationConnectSlack />,
    },
    {
      path: "/connectLinear",
      element: <IntegrationConnectLinear />,
    },
    {
      path: "/connectJira",
      element: <IntegrationConnectJira />,
    },
    {
      path: "/x/shared/:sharedEntityName/:campaignId",
      element: <XShared />,
    },
  ];

  if (!isCustomDomain) {
    routes.push({
      path: "/onboarding",
      element: <Onboarding />,
      children: [
        {
          path: "service",
          element: <ServiceOnboardingSetStepContainer />,
        },
        {
          path: "marketing",
          element: <MarketingOnboardingSetStepContainer />,
        },
      ],
    });
  }

  return routes;
};
const RouteComponent = () => {
  const router = createBrowserRouter(createRouteConfig());

  return <RouterProvider router={router} />;
};

export default React.memo(RouteComponent);
